import React, { useReducer, useEffect } from 'react';
import {
    makeStyles, Button, useTheme, useMediaQuery, Chip, Tooltip,
} from '@material-ui/core';
import Select from 'components/widgets/Select';
import Table from 'components/widgets/Table';
import Split from 'react-split';
import ModalUtils from 'utils/ModalUtils';
import InputSearch from 'components/widgets/InputSearch';
import Header from 'components/widgets/Header';
import AccountingStyles from 'styles/modules/accounting/AccountingStyles';
import Container from 'components/widgets/Container';
import clsx from 'clsx';
import { Form } from 'react-bootstrap';
import update from 'immutability-helper';
import { useQuery } from '@apollo/client';
import NumberUtils from 'lib/NumberUtils';
import StringUtils from 'lib/StringUtils';
import ArrayUtils from 'lib/ArrayUtils';
import DateUtils from 'lib/DateUtils';
import AccountingUtils from 'utils/AccountingUtils';
import { LocalAtmIcon } from 'components/icons';
import LotQuery from 'services/graphQL/query/LotQuery';
import AccountReceivableQuery from 'services/graphQL/query/accounting/AccountReceivableQuery';
import AccountReceivablesMapper from 'services/mapData/AccountReceivablesMapper';
import CashierPaymentList from 'components/modules/accounting/accountsReceivable/list/CashierPaymentList';
import LabelValue from 'components/widgets/LabelValue';
import { useHistory } from 'react-router-dom';
import { modules } from 'utils/enum/modules';
import LotsCategory, { LotDefaultskey } from 'utils/enum/LotsCategory';
import { AccountingSubModules, AccountsReceivableEntities } from 'utils/enum/AccountingEnum';
import { DataSort, FetchPolicy, ALL_LOTS } from 'utils/enum/Core';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import { DealStatus, DealType } from 'utils/enum/DealEnum';

const useStyle = makeStyles((theme) => ({
    ...AccountingStyles.mainList(theme),
    table: {
        '& div.rt-thead': {
            background: `${theme.palette.background.lightPeriwinkle} !important`,
        },
    },
}));

const baseRoute = `/${modules.ACCOUNTING}/${AccountingSubModules.ACCOUNTS_RECEIVABLE}/${AccountsReceivableEntities.DEALS}`;
const ACTION_TYPES = {
    SET_NEW: 'setNew',
    LOAD_MORE: 'setLoadMore',
    SET_PARAMS: 'setParams',
    SET_TABLE: 'setTable',
    SET_SELECTED_ROW: 'setSelectedRow',
    CLOSE_SCREEN: 'setCloseScreen',
    SET_INITIAL_STATE: 'setInitialState',
    ENABLE_PAY_NEAR_ME_BUTTON: 'enablePayNearMeButton',
    SET_DISPLAY_SETTINGS: 'setDisplaySettings',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.SET_NEW: {
        return {
            ...state,
            isEditing: false,
            openTakePayment: true,
        };
    }
    case ACTION_TYPES.SET_SELECTED_ROW: {
        return {
            ...state,
            selectedRow: action.value,
        };
    }
    case ACTION_TYPES.ENABLE_PAY_NEAR_ME_BUTTON: {
        return update(state, {
            enablePayNearMeButton: { $set: action.value },
        });
    }
    case ACTION_TYPES.SET_TABLE: {
        return {
            ...state,
            table: action.value,
        };
    }
    case ACTION_TYPES.SET_PARAMS: {
        return {
            ...state,
            table: {
                records: [],
                totalCount: 0,
            },
            params: action.value,
            selectedRow: null,
        };
    }
    case ACTION_TYPES.LOAD_MORE: {
        return {
            ...state,
            params: action.value,
        };
    }
    case ACTION_TYPES.CLOSE_SCREEN: {
        return {
            ...state,
            selectedRow: null,
            isEditing: false,
            openTakePayment: false,
        };
    }
    case ACTION_TYPES.SET_INITIAL_STATE: {
        return action.value;
    }
    case ACTION_TYPES.SET_DISPLAY_SETTINGS: {
        return {
            ...state,
            displaySettings: action.value,
        };
    }
    default: return state;
    }
};

const keyStore = new KeyStore();
const allDealType = 'ALL DEAL TYPES';
const AccountReceivableDealList = () => {
    const ACCOUNTING_ACCOUNT_RECEIVABLE_COLLECTION_AND_PAYOUTS = keyStore.hasPermission(Permission.ACCOUNTING_ACCOUNT_RECEIVABLE_COLLECTION_AND_PAYOUTS);

    const history = useHistory();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const initState = {
        params: {
            paginate: {
                init: 0,
                limit: 25,
            },
            search: '',
            includeBalanced: false,
            dealType: allDealType,
        },
        table: {
            records: [],
            totalCount: 0,
        },
        selectedRow: null,
        openTakePayment: false,
        provider: null,
        enablePayNearMeButton: false,
        displaySettings: null,
    };

    const classes = useStyle();

    const [state, dispatch] = useReducer(reducer, initState);
    const {
        params, table, selectedRow, displaySettings,
    } = state;

    const {
        columns: displayColumns = [],
        noDisplayCashDeals = false,
        noDisplayWholesaleDeals = false,
    } = displaySettings ?? {};

    const displayedColumns = AccountingUtils
        .getDefaultAccountsReceivableColumns()
        .filter((c) => (displayColumns.length === 0 && c.display) || displayColumns.find((d) => d.title === c.title)?.display);

    const {
        data, loading, error,
    } = useQuery(AccountReceivableQuery.getUnpaidDealList(
        [...new Set(
            [
                'cashDownPayment',
                'amountDue',
                'collectedAmount',
                ...displayedColumns
                    .reduce((a, b) => {
                        if (b.additionalColumns) return [...a, ...b.additionalColumns];
                        return [...a, b.column];
                    }, []),
            ],
        )],
    ), {
        variables: {
            paginate: params.paginate,
            filter: {
                search: params.search,
                includeBalanced: params.includeBalanced,
                ...(params.dealType !== allDealType ? {
                    dealType: params.dealType,
                } : {}),
            },
            sort: [{ fieldName: 'accountNumber', dir: DataSort.DESC }],
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        skip: displaySettings == null,
    });

    const { ACCOUNT_RECEIVABLE_DISPLAY_SETTINGS } = LotDefaultskey;
    const {
        data: settings,
        loading: settingsLoading,
        error: settingsError,
    } = useQuery(LotQuery.GET_SETTINGS, {
        variables: {
            category: LotsCategory.ACCOUNTING,
            lotName: ALL_LOTS,
            key: [ACCOUNT_RECEIVABLE_DISPLAY_SETTINGS],
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
            return;
        }

        if (!loading && data) {
            const { records } = table;
            const { getUnpaidDealList } = data;

            const mapRecords = AccountReceivablesMapper.mapDeals(getUnpaidDealList.data, displayedColumns);
            records.push(...mapRecords);
            dispatch({
                type: ACTION_TYPES.SET_TABLE,
                value: {
                    records,
                    totalCount: getUnpaidDealList.totalCount,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, error]);

    useEffect(() => {
        if (settingsError) {
            ModalUtils.errorMessage(settingsError?.graphQLErrors);
            return;
        }

        if (!settingsLoading && settings) {
            const { getSettings } = settings;
            if (ArrayUtils.isNotEmpty(getSettings)) {
                const currentSettings = getSettings.find((setting) => setting.key === ACCOUNT_RECEIVABLE_DISPLAY_SETTINGS)?.value ?? '';
                if (!StringUtils.isEmpty(currentSettings)) {
                    try {
                        dispatch({
                            type: ACTION_TYPES.SET_DISPLAY_SETTINGS,
                            value: JSON.parse(currentSettings),
                        });
                    } catch (_) {
                        ModalUtils.errorMessage(['Error loading display settings']);
                    }
                }

                return;
            }

            dispatch({
                type: ACTION_TYPES.SET_DISPLAY_SETTINGS,
                value: {},
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, settingsError]);

    const onFilterChange = (param, value) => {
        dispatch({
            type: ACTION_TYPES.SET_PARAMS,
            value: {
                ...params,
                [param]: value,
                paginate: {
                    ...params.paginate,
                    init: 0,
                },
            },
        });
    };

    const onSearch = (text) => {
        const { paginate } = params;

        dispatch({
            type: ACTION_TYPES.SET_PARAMS,
            value: {
                ...params,
                search: text,
                paginate: {
                    ...paginate,
                    init: 0,
                },
            },
        });
    };

    const loadMore = () => {
        const currentOffset = table?.records?.length || 0;
        const { paginate } = params;

        dispatch({
            type: ACTION_TYPES.LOAD_MORE,
            value: {
                ...params,
                paginate: {
                    ...paginate,
                    init: currentOffset,
                },
            },
        });
    };

    const columns = [
        {
            minWidth: 30,
            width: 30,
            headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
            className: clsx(classes.columnStyle, classes.columnLeft),
            Cell: (cell) => cell.viewIndex + 1,
        },
        ...(displayedColumns.map((c) => ({
            Header: c.title,
            id: c.column,
            accessor: c.column,
            ...(c.column === 'accountNumber' ? {
                minWidth: 100,
                width: 100,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnCenter),
                className: clsx(classes.columnStyle, classes.columnCenter),
            } : {}),
            ...(c.column === 'remainingDue' ? {
                minWidth: 120,
                width: 120,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
                className: clsx(classes.columnStyle, classes.columnRight),
                Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
            } : {}),
            ...(c.column === 'amountDue' ? {
                minWidth: 100,
                width: 100,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
                className: clsx(classes.columnStyle, classes.columnRight),
                Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
            } : {}),
            ...(c.column === 'cashDownPayment' ? {
                minWidth: 130,
                width: 130,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
                className: clsx(classes.columnStyle, classes.columnRight),
                Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
            } : {}),

            ...(c.column === 'cpiFirstPaymentAtSigning' ? {
                minWidth: 130,
                width: 130,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
                className: clsx(classes.columnStyle, classes.columnRight),
                Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
            } : {}),
            ...(c.column === 'leaseTotalDueAtSigning' ? {
                minWidth: 130,
                width: 130,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
                className: clsx(classes.columnStyle, classes.columnRight),
                Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
            } : {}),
            ...(c.column === 'reserveAmount' ? {
                minWidth: 90,
                width: 90,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
                className: clsx(classes.columnStyle, classes.columnRight),
                Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
            } : {}),
            ...(c.column === 'soldDate' ? {
                minWidth: 100,
                width: 100,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnCenter),
                className: clsx(classes.columnStyle, classes.columnCenter),
                Cell: ({ value }) => DateUtils.getOnlyDate(value),
            } : {}),
            ...(c.column === 'buyer' ? {
                minWidth: 160,
                width: 160,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
                className: clsx(classes.columnStyle, classes.columnLeft),
            } : {}),
            ...(c.column === 'salesPerson1' ? {
                minWidth: 160,
                width: 160,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
                className: clsx(classes.columnStyle, classes.columnLeft),
            } : {}),
            ...(c.column === 'financeManager' ? {
                minWidth: 160,
                width: 160,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
                className: clsx(classes.columnStyle, classes.columnLeft),
            } : {}),
            ...(c.column === 'dealType' ? {
                minWidth: 120,
                width: 120,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
                className: clsx(classes.columnStyle, classes.columnLeft),
            } : {}),
            ...(c.column === 'term' ? {
                minWidth: 60,
                width: 60,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
                className: clsx(classes.columnStyle, classes.columnRight),
            } : {}),
            ...(c.column === 'paymentAmount' ? {
                minWidth: 100,
                width: 100,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnRight),
                className: clsx(classes.columnStyle, classes.columnRight),
                Cell: ({ value }) => NumberUtils.applyCurrencyFormat(value),
            } : {}),
            ...(c.column === 'firstPaymentDue' ? {
                minWidth: 150,
                width: 150,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnCenter),
                className: clsx(classes.columnStyle, classes.columnCenter),
                Cell: ({ value }) => DateUtils.getOnlyDate(value),
            } : {}),
            ...(c.column === 'financeCompany' ? {
                minWidth: 150,
                width: 150,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnLeft),
                className: clsx(classes.columnStyle, classes.columnLeft),
            } : {}),
            ...(c.column === 'status' ? {
                minWidth: 120,
                width: 120,
                headerClassName: clsx(classes.columnHeaderStyle, classes.columnCenter),
                className: clsx(classes.columnStyle, classes.columnCenter),
                Cell: (cellData) => {
                    const { original: { status } } = cellData;
                    let badgeStatus = classes.primary;
                    if (status?.toLowerCase() === DealStatus.DELETED.toLowerCase()) badgeStatus = classes.badgeVoid;

                    return <Chip size="small" label={status} className={badgeStatus} />;
                },
            } : {}),
        }))),
    ];

    return (
        <div className={classes.main}>
            <Header>
                <div className={classes.containerFilterSearch}>
                    <InputSearch
                        customClasses={clsx(classes.search, isMobile ? classes.searchFull : '')}
                        initialSearch={params.search || ''}
                        onSearch={onSearch}
                    />
                    <Select
                        nowrap
                        loading={false}
                        size="sm"
                        name="dealType"
                        placeholder="Deal Type"
                        className={classes.input}
                        onChange={(name, value) => onFilterChange(name, value)}
                        value={params.dealType}
                        options={
                            [allDealType, ...Object.values(DealType)]
                                .filter((f) => {
                                    if (noDisplayCashDeals && f === DealType.CASH) return false;
                                    if (noDisplayWholesaleDeals && f === DealType.WHOLESALE) return false;

                                    return true;
                                })
                                .map((f) => ({
                                    value: f,
                                    label: f,
                                }))
                        }
                    />
                    <Form.Group controlId="formBasicCheckbox" className={classes.alignContent}>
                        <Form.Check
                            checked={params.includeBalanced}
                            type="checkbox"
                            label="Include paid & deleted accounts"
                            className={classes.labelFont}
                            onChange={(e) => onFilterChange('includeBalanced', e.target.checked)}
                        />
                    </Form.Group>
                    <LabelValue
                        label="Items"
                        value={table.totalCount}
                    />
                </div>
                <div className={classes.buttonSpacing}>
                    {ACCOUNTING_ACCOUNT_RECEIVABLE_COLLECTION_AND_PAYOUTS
                    && (
                        <>
                            <Tooltip title="Take Payment" placement="top-start">
                                <span>
                                    <Button
                                        onClick={() => {
                                            dispatch({ type: ACTION_TYPES.SET_NEW });
                                            history.push(`${baseRoute}/${selectedRow?.accountNumber}/payment`);
                                        }}
                                        variant="outlined"
                                        startIcon={<LocalAtmIcon />}
                                        disabled={loading || !selectedRow}
                                        size="small"
                                    >
                                        Take Payment
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Take Payment" placement="top-start">
                                <span>
                                    <Button
                                        onClick={() => {
                                            dispatch({ type: ACTION_TYPES.SET_NEW });
                                            history.push(`${baseRoute}/${selectedRow?.accountNumber}/electronic-payment`);
                                        }}
                                        variant="outlined"
                                        startIcon={<LocalAtmIcon />}
                                        disabled={loading || !selectedRow}
                                        size="small"
                                    >
                                        Electronic Payment
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    )}
                </div>
            </Header>
            <Container className={classes.containerSplit}>
                {displaySettings != null && (
                    <Split
                        sizes={selectedRow && selectedRow.accountNumber ? [60, 40] : [96, 4]}
                        className={clsx(classes.split, classes.table)}
                        direction="vertical"
                        minSize={0}
                    >
                        <Table
                            data={table?.records}
                            columns={columns}
                            cursor="default"
                            load={loading}
                            totalRecords={table?.totalCount}
                            loadMore={loadMore}
                            rowSelected
                            className="-highlight"
                            getTrProps={(_, rowInfo) => {
                                const record = rowInfo.original;
                                return {
                                    onDoubleClick: () => {
                                        dispatch({ type: ACTION_TYPES.SET_SELECTED_ROW, value: record });
                                    },
                                    onClick: () => {
                                        dispatch({ type: ACTION_TYPES.SET_SELECTED_ROW, value: record });
                                    },
                                };
                            }}
                        />
                        <div>
                            {selectedRow && selectedRow.accountNumber && (
                                <CashierPaymentList
                                    accountNumber={selectedRow.accountNumber}
                                />
                            )}
                        </div>
                    </Split>
                )}
            </Container>
        </div>
    );
};

export default AccountReceivableDealList;
